import { useCapability } from '@app-iptv/device/react'
import { BrandProvider, getCurrentFocusKey, setFocus, TierProvider } from '@app-iptv/ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'


import { resetBootSnapshot, startBootSnapshot } from './boot-snapshot'
import { resolveBrandColors } from './branding-read'
import { CatalogSyncIndicator } from './components/CatalogSyncIndicator'
import { LivePip } from './components/LivePip'
import { LongSessionPrompt } from './components/LongSessionPrompt'
import { ReminderToastGlobal } from './components/ReminderToastGlobal'
import { TrialGate } from './components/TrialGate'
import { useBandwidthRefresh } from './hooks/useBandwidthRefresh'
import { useBrandingRefresh } from './hooks/useBrandingRefresh'
import { useCatalogRefresh } from './hooks/useCatalogRefresh'
import { useCatalogRefreshProviderKey } from './hooks/useCatalogRefreshProviderKey'
import { engineHasProxy, proxyTrackingFallback } from './lib/query-proxy-fallback'
import { LivePlayerProvider } from './live-player/LivePlayerProvider'
import { startLongSessionCleaner, stopLongSessionCleaner } from './long-session-cleaner'
import { bindScopeIsolation } from './provider-isolation'
import { currentScope } from './scope'
import { applyFontScale, useSettingsStore } from './settings-store'
import { useSessionStore } from './store'
import { setSyncAppliedHandler } from './sync'
import { partitionRailsByFocus, type RefetchRail } from './sync/refetch-focus-guard'

/** Log dev-only — em prod gera ruído no DevTools de TVs (e em alguns
 *  cenários impacta performance). Comentário/diagnóstico só em build dev.
 *
 *  ATENÇÃO: NÃO usar replace_all('console.warn', 'devLog') no editor —
 *  bate aqui dentro e gera recursão infinita. Manter `console.warn`
 *  literal nesta linha. */
const devLog = (...args: unknown[]): void => {
   
  if (import.meta.env.DEV) console.warn(...args)
}

export function AppProviders({ children }: { children: ReactNode }) {
  // Tier + i18n + spatial nav já foram inicializados em bootstrap() antes do React montar.
  // Tier ainda é exposto pelo TierProvider pra UI (data-tier), mas
  // QueryClient lê capabilities granulares — se rede oscilar pra poor
  // ou user trocar override, retry/gcTime reagem (nova instância).
  const tier = useCapability('cpuTier')
  const retryCount = useCapability('retryCount')
  const retryDelayMs = useCapability('retryDelayMs')
  const rqGcTimeMs = useCapability('rqGcTimeMs')
  const branding = useSessionStore((s) => s.branding)
  const isAuthed = useSessionStore((s) => s.isAuthed)
  const providerCode = useSessionStore((s) => s.providerCode)
  const directListName = useSessionStore((s) => s.direct?.listName ?? null)

  // Aplica fontScale + reduceMotion no <html>. Settings-store é persistido
  // (sobrevive logout) e os tokens CSS já reagem aos data-attrs / vars.
  //
  // O --font-scale em si já foi escrito pelo settings-store (no boot e em cada
  // troca — ver applyFontScale, que também derruba o cache de tokens): a
  // geometria do card DERIVA dele e o useMemo dos drills lê no RENDER, cedo
  // demais pra um efeito. Este efeito é a rede de segurança idempotente (HMR,
  // remonta do provider) e o dono do reduceMotion.
  //
  // applyFontScale aplica via ATRIBUTO (`dataset.fontScale`), não mais
  // `style.setProperty`: o multiplicador numérico (0.9/1.15/1.3) agora mora
  // só em packages/ui/src/tokens.css
  // (`:root[data-font-scale='small'|'large'|'xlarge']`) — troca de mecanismo,
  // não de comportamento (mesmo recompute de cascade, mesma reatividade).
  // Habilita o build LEGACY de TV (Chromium 38, sem var()) a achatar
  // `--font-scale` por MULTIPLICAÇÃO DE SELETOR (o mesmo truque de
  // `data-tier`/`data-reduce-motion`) em vez de precisar de um shim — ver
  // apps/tv/postcss/legacy-token-resolver.mjs. `normal` não seta nada (o
  // default do :root já vale 1); os outros 3 valores do enum viram atributo
  // direto.
  const fontScale = useSettingsStore((s) => s.fontScale)
  const reduceMotion = useSettingsStore((s) => s.reduceMotion)
  useEffect(() => {
    const root = document.documentElement
    applyFontScale(fontScale)
    if (reduceMotion) root.dataset['reduceMotion'] = 'true'
    else delete root.dataset['reduceMotion']
  }, [fontScale, reduceMotion])

  // QueryClient capability-aware. Snapshot inicial pra criar a instância
  // (recriar invalidaria TODO o cache em memória — race com queries em
  // voo). Mudanças subsequentes em retry/gcTime aplicadas in-place via
  // `setDefaultOptions` no useEffect abaixo, preservando cache.
  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 60 * 1000,
          gcTime: rqGcTimeMs,
          refetchOnWindowFocus: false,
          retry: retryCount,
          retryDelay: retryDelayMs,
          // Piso Chr38 (webOS 3.5) não tem Proxy → o tracked-query do TanStack v5
          // lançaria `ReferenceError: Proxy is not defined` em TODO useQuery no
          // render. Fallback por CAPABILITY (ver lib/query-proxy-fallback.ts).
          ...proxyTrackingFallback(engineHasProxy()),
        },
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Atualiza defaults in-place quando capabilities mudam (override de tier
  // em Settings, rede oscila). Não recria a instância — só queries futuras
  // (e queries que aceitarem novos defaults via re-fetch) usam os novos
  // valores. Cache existente preservado.
  useEffect(() => {
    queryClient.setDefaultOptions({
      queries: {
        staleTime: 60 * 1000,
        gcTime: rqGcTimeMs,
        refetchOnWindowFocus: false,
        retry: retryCount,
        retryDelay: retryDelayMs,
        // Mantém o fallback de Proxy ao reaplicar defaults (senão o piso Chr38
        // voltaria a tentar o tracked-query → crash). Ver bloco de criação acima.
        ...proxyTrackingFallback(engineHasProxy()),
      },
    })
  }, [queryClient, retryCount, retryDelayMs, rqGcTimeMs])

  // Long-session cleaner (#7 do roadmap pós-MVP). Inicia 1× no mount.
  // Cleanup ativo: 1×/h libera IDB stores expirados + RQ queries inativas.
  // Telemetria: dispara QoE quando longSessionMode muda.
  // Prompt de reload: <LongSessionPrompt> escuta e mostra modal SE
  // health check (heap pressure) provar problema. Sem evidência = sem
  // prompt. Capabilities de UI NÃO degradam — cleanup é a defesa real.
  useEffect(() => {
    startLongSessionCleaner(queryClient)
    return () => {
      // Em prod AppProviders nunca desmonta (root), mas dev/HMR sim.
      // Sem stop, Vite Fast Refresh empilha subscribers + timers a
      // cada save (M1 do code review).
      stopLongSessionCleaner()
    }
  }, [queryClient])

  // ===== Sync cross-device: refresh silencioso pós-pull (TV-first) =====
  // Quando o puller traz dados de outro device, invalida as queries da Home/
  // "continuar assistindo"/favoritos/"já assistidos" do SCOPE ATUAL pra a UI
  // refletir sem reload. Sync silencioso (decisão de produto): só refresh, sem
  // badge/spinner.
  //
  // CUIDADO TV-first (gate ux-tv): NUNCA mexer numa rail que está SOB O FOCO — em
  // TV, reordenar/remover o card focado rouba o controle remoto (foco órfão) ou
  // desloca o cursor. Então: invalida agora as rails livres; ADIA a rail focada
  // até o foco sair dela (re-tenta em intervalo curto). `invalidateQueries` mantém
  // o `data` anterior durante o refetch → sem flash de skeleton nas rails que já
  // tinham conteúdo.
  useEffect(() => {
    // rail → prefixos da focus-key dos cards. Se o foco começa com algum prefixo,
    // a rail está "ocupada" e a invalidação dela espera (S5: NUNCA mexer no card
    // focado — reordenar/remover rouba o controle ou cria foco órfão).
    //
    // Algumas rails têm a MESMA query alimentando 2 abas com prefixos diferentes:
    // "continuar assistindo" de Séries/Filmes aparece na aba Descobrir (`car-cont-*`)
    // E na aba Minha Lista (`mylist-cont-*`) — por isso `focusPrefixes` é uma lista.
    // Antes só a home-continue (`pos-`) tinha guard; as rails de Séries/Filmes
    // ficaram sem proteção quando o fix das séries (#446) unificou os leitores.
    // `focusPrefixes` vazio = rail sem foco próprio → invalida sempre.
    const RAILS: RefetchRail[] = [
      { parts: ['home-continue'], focusPrefixes: ['pos-'] },
      { parts: ['favorites'], focusPrefixes: ['fav-'] },
      { parts: ['recent-channels'], focusPrefixes: ['recent-'] },
      // series-finished não tem rail própria na Home → invalida sempre.
      { parts: ['series-finished'], focusPrefixes: [] },
      // Continuar assistindo de Séries (SeriesScreen) — Descobrir + Minha Lista.
      { parts: ['series-continue'], focusPrefixes: ['car-cont-', 'mylist-cont-'] },
      // Continuar assistindo de Filmes (MoviesScreen) — Descobrir + Minha Lista.
      { parts: ['vod-continue'], focusPrefixes: ['car-cont-', 'mylist-cont-'] },
      // "Continuar assistindo" da superfície MOBILE (MobileHome). Touch, sem foco de
      // controle → invalida sempre. Até 10/10/2026 o celular não puxava nada, então a
      // falta dela não aparecia; com o sync ligado lá, o histórico que chega da TV
      // precisa repintar o rail.
      { parts: ['home-positions'], focusPrefixes: [] },
    ]
    let pendingTimer: ReturnType<typeof setTimeout> | null = null

    const invalidate = (scope: string, parts: readonly string[]): void => {
      void queryClient.invalidateQueries({ queryKey: ['scope', scope, ...parts] })
    }

    const applyRespectingFocus = (attempt = 0): void => {
      // Cancela uma cadeia adiada anterior (pull novo manda na atualização) —
      // evita 2 cadeias de re-tentativa concorrentes.
      if (pendingTimer) {
        clearTimeout(pendingTimer)
        pendingTimer = null
      }
      const scope = currentScope()
      if (!scope) return
      // Decisão (qual rail está sob foco) = função pura testável. Aqui só os
      // efeitos colaterais (invalidate + reancorar foco).
      const { free, deferred } = partitionRailsByFocus(RAILS, getCurrentFocusKey())
      for (const rail of free) invalidate(scope, rail.parts) // rail livre → atualiza na hora
      // Rails ocupadas: re-tenta até o foco sair (ou desiste após ~10s pra não
      // segurar pra sempre — o próximo pull/route change recupera de qualquer forma).
      if (deferred.length > 0 && attempt < 5) {
        pendingTimer = setTimeout(() => applyRespectingFocus(attempt + 1), 2000)
      } else if (deferred.length > 0) {
        // Desistiu de esperar: aplica + reancora o foco se ele virou órfão.
        const before = getCurrentFocusKey()
        for (const rail of deferred) invalidate(scope, rail.parts)
        // Rede de segurança: se o card focado some no refetch, o Norigin fica com
        // key órfã (folhas filhas do ROOT não têm auto-restore). Reancora num
        // alvo sempre presente (a sidebar de navegação).
        setTimeout(() => {
          if (getCurrentFocusKey() === before) setFocus('nav-home')
        }, 250)
      }
    }

    const unsub = setSyncAppliedHandler(() => applyRespectingFocus(0))
    return () => {
      unsub()
      if (pendingTimer) clearTimeout(pendingTimer)
    }
  }, [queryClient])

  // ===== Isolamento por scope (provider+profile) =====
  // Quando o scope muda (troca de provider OU de perfil), zera o cache
  // EM MEMÓRIA do React Query. Caches PERSISTIDOS (IDB snapshot,
  // local-db, vod-info-store, series-info-store) NÃO são tocados —
  // cada (provider, perfil) tem seu scope nas keys e seus dados
  // coexistem no IDB. Voltar pra um scope anterior reusa tudo.
  //
  // ── QUANDO zera (10/10/2026) ──
  // Era um `useEffect([scopeKey])`, e efeito roda DEPOIS do render. Nesse render a tela
  // que continua montada (a Home do mobile atravessa a adoção do perfil sem desmontar)
  // já tinha trocado a queryKey e criado a query do scope NOVO; o `clear()` do efeito
  // apagava do cache justamente essa query, com o observer ainda pendurado nela. Daí
  // nenhum `invalidateQueries` a alcançava: o histórico que o pull trazia da TV ficava
  // no IDB e o "Continuar" só aparecia depois de recarregar. Agora o zerar roda DENTRO
  // do `set()` do store (assinatura síncrona), ANTES de o React renderizar o scope
  // novo: as queries do scope novo nascem depois do zerar e ficam no cache.
  useEffect(() => bindScopeIsolation(queryClient, useSessionStore, devLog), [queryClient])

  // Snapshot + bandwidth fundidos (Fase 3.7): pós-login o app baixa o catálogo
  // do parceiro e mede banda durante o download (todo byte serve a 2 fins). Se
  // snapshot recente já está no IndexedDB, nem baixa.
  //
  // Isto era um OVERLAY full-screen que pegava o foco (`setFocus('bw-skip')`) e
  // segurava a TV por >=4,0s. Virou TAREFA (`boot-snapshot.ts`) + indicador
  // passivo (`<CatalogSyncIndicator/>`). O ciclo de vida do DADO não está mais
  // amarrado ao ciclo de vida do PIXEL: esconder o indicador não cancela nada.
  //
  // Deps `[isAuthed]` de propósito — MESMO gatilho de antes (1× por transição
  // de login). Trocar de provider sem logout continua não re-disparando o fetch
  // de boot; quem cobre esse caso é o `useCatalogRefresh` abaixo.
  useEffect(() => {
    if (!isAuthed) {
      resetBootSnapshot()
      return
    }
    startBootSnapshot()
  }, [isAuthed])

  // Refresh client-side (#8): substitui a antiga revalidação 1×/sessão.
  // Hook orquestra polling 30min com jitter + visibility refresh ao
  // voltar de standby da TV. Decisões de discovery em
  // `docs/specs/roadmap-pos-mvp.md` §#8.
  // Provider key (snapshot é shared cross-profile dentro do mesmo
  // provider — mudança de perfil NÃO reseta o timer).
  //
  // ⚠️ ESTE É O INTERRUPTOR GERAL DO CATÁLOGO — com a chave `null` o hook não
  // agenda NADA (sem tick de 5s, sem poll de 30min, sem refresh ao voltar do
  // standby). A condição mora em `useCatalogRefreshProviderKey` (e não inline
  // aqui) porque a versão inline foi a regressão: ela dizia
  // `!bootSnapshotDone`, e `phase` nasce `idle` — qualquer fase não-terminal
  // deixava o app SEM CATÁLOGO pra sempre, já que o effect de boot acima tem
  // deps `[isAuthed]` e não re-roda. A pergunta certa é afirmativa ("tem boot
  // em voo?"), que responde `false` no fim E no "nunca teve". Ver o fileoverview
  // daquele módulo — e o teste que o exercita direto, sem transcrever o gate.
  const refreshProviderKey = useCatalogRefreshProviderKey({
    isAuthed,
    providerCode,
    directListName,
  })
  useCatalogRefresh({ providerKey: refreshProviderKey })

  // Re-mensuração de banda em background a cada 4h. Capabilities
  // derivadas reagem (player quality, prefetch, image preset) quando
  // Wi-Fi muda durante sessões longas. Hysteresis anti-flapping +
  // skip durante playback/standby. Detalhes em useBandwidthRefresh.
  useBandwidthRefresh()

  // Refresh de branding em background — pega mudanças do admin do
  // parceiro (cor, logo, name) sem exigir logout do user. 1 GET por
  // boot quando há provider ativo. Detalhes em useBrandingRefresh.
  useBrandingRefresh()

  // Tolera o alias LEGADO `color` do branding persistido (ver branding-read.ts).
  const brandColors = resolveBrandColors(branding)

  return (
    <QueryClientProvider client={queryClient}>
      <TierProvider tier={tier}>
        {/* primaryColor/secondaryColor null → BrandProvider remove
            override → --brand-* cai pro fallback var(--app-*) do
            tokens.css. Garante que pre-provider screens / direct mode /
            logout não herdem cor de parceiro de sessão anterior.
            resolveBrandColors tolera o alias LEGADO `color` (branding
            antigo `{ color }` no disco de aparelhos reais — ver
            branding-read.ts). */}
        <BrandProvider
          primaryColor={brandColors.primaryColor}
          secondaryColor={brandColors.secondaryColor}
        >
          {/* Singleton de player ao vivo. Vive dentro de QueryClient pra
              poder usar api (que precisa de auth via sessionStore — sem
              dependência direta no QueryClient). Hero (LiveScreen) e
              PlayerScreen (modo live) compartilham a mesma instância
              MediaPlayer — handoff via attachTo, sem reload. */}
          <LivePlayerProvider>
            {children}
            {/* Status PASSIVO do fetch de boot do catálogo. Não é focável, não
                cobre conteúdo, não empurra layout, não bloqueia navegação —
                irmão de {children}, nunca cortina por cima. Self-managed:
                aparece/some lendo `boot-snapshot`. */}
            <CatalogSyncIndicator />
            {/* Long-session prompt (#7 do roadmap pós-MVP). Auto-mostra
                quando watcher detecta sessão > 8h. Self-managed. */}
            <LongSessionPrompt />
            {/* Toast global de lembrete EPG. Aparece em qualquer rota
                quando lembrete dispara (Home, Live, Search, Settings,
                Player). Self-managed. ⚠️ ÚNICA instância de useFiringReminder
                no app (consume é destrutivo). */}
            <ReminderToastGlobal />
            {/* Picture-in-Picture flutuante. Aberto via botão "Espiar"
                do toast — reusa singleton player. Self-managed via
                pip-store. Z 150 (abaixo do toast 200, acima do conteúdo). */}
            <LivePip />
            {/* Gate global de trial (κ+1): bloqueia o APP INTEIRO quando o tempo
                gratuito do dia esgota. Inescapável (só comprar ou reiniciar) e
                z-index máximo — cobre player, toast e pip. Checa no boot +
                polling pra liberação instantânea pós-pagamento. Self-managed. */}
            <TrialGate />
          </LivePlayerProvider>
        </BrandProvider>
      </TierProvider>
    </QueryClientProvider>
  )
}
